import { getAuthToken } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Update thread participants
 * @param {string} threadId - The thread ID
 * @param {Object} newParticipant - The new participant data
 * @returns {Promise<Object>} Update result
 */
export const updateThreadParticipants = async (threadId, newParticipant) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/participants`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newParticipant)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating thread participants:', error);
    throw error;
  }
};

/**
 * Create a new thread
 * @param {Object} threadData - The thread data
 * @returns {Promise<Object>} Created thread data
 */
export const createThread = async (threadData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(threadData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating thread:', error);
    throw error;
  }
};

/**
 * Add a system message to a thread
 * @param {string} threadId - The thread ID
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} Created message data
 */
export const addSystemMessage = async (threadId, messageData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/system-messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding system message:', error);
    throw error;
  }
};

/**
 * Get thread by ID
 * @param {string} threadId - The thread ID
 * @returns {Promise<Object>} Thread data
 */
export const getThread = async (threadId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching thread:', error);
    throw error;
  }
};

/**
 * Get thread messages
 * @param {string} threadId - The thread ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of thread messages
 */
export const getThreadMessages = async (threadId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.before) {
      queryParams.append('before', options.before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/messages?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching thread messages:', error);
    throw error;
  }
};

/**
 * Send a secure message
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} Sent message data
 */
export const sendSecureMessageClient = async (messageData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending secure message:', error);
    throw error;
  }
};

/**
 * Save a draft message
 * @param {Object} draftData - The draft data
 * @returns {Promise<Object>} Saved draft data
 */
export const saveDraft = async (draftData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/drafts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(draftData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving draft:', error);
    throw error;
  }
};

/**
 * Get current user data
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Current user data
 */
export const getCurrentUserData = async (userId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/${userId}/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching current user data:', error);
    throw error;
  }
};

/**
 * Fetch mailbox data for the current user
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Mailbox data including threads and messages
 */
export const fetchMailboxData = async (userId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/mailbox/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching mailbox data:', error);
    throw error;
  }
};

/**
 * Fetch messages for a specific thread
 * @param {string} threadId - The thread ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of messages for the thread
 */
export const fetchMessagesForThread = async (threadId, options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.before) {
      queryParams.append('before', options.before);
    }

    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/messages?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching messages for thread:', error);
    throw error;
  }
};

/**
 * Fetch users by their IDs
 * @param {Array<string>} userIds - Array of user IDs
 * @returns {Promise<Array>} Array of user data
 */
export const fetchUsersByIds = async (userIds) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/users/batch`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userIds })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching users by IDs:', error);
    throw error;
  }
};

/**
 * Delete a draft message
 * @param {string} draftId - The draft ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteDraftMessage = async (draftId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/drafts/${draftId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting draft message:', error);
    throw error;
  }
};

/**
 * Move a thread to trash
 * @param {string} threadId - The thread ID
 * @returns {Promise<Object>} Move result
 */
export const moveThreadToTrash = async (threadId) => {
  try {
    const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/trash`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error moving thread to trash:', error);
    throw error;
  }
};

/**
 * Get thread details
 * @param {string} threadId - The thread ID
 * @returns {Promise<Object>} Thread details
 */
export const getThreadDetails = async (threadId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching thread details:', error);
    throw error;
  }
};

/**
 * Send a message to a thread
 * @param {string} threadId - The thread ID
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} Sent message data
 */
export const sendMessage = async (threadId, messageData) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Archive a thread
 * @param {string} threadId - The thread ID
 * @returns {Promise<Object>} Archive result
 */
export const archiveThread = async (threadId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/archive`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error archiving thread:', error);
    throw error;
  }
};

/**
 * Update thread subject
 * @param {string} threadId - The thread ID
 * @param {string} subject - The new subject
 * @returns {Promise<Object>} Update result
 */
export const updateThreadSubject = async (threadId, subject) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/subject`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ subject })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating thread subject:', error);
    throw error;
  }
};

/**
 * Add participants to a thread
 * @param {string} threadId - The thread ID
 * @param {Array} participants - Array of participant objects
 * @returns {Promise<Object>} Update result
 */
export const addParticipantsToThread = async (threadId, participants) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/participants`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ participants })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error adding participants to thread:', error);
    throw error;
  }
};

/**
 * Remove participant from a thread
 * @param {string} threadId - The thread ID
 * @param {string} participantId - The participant ID to remove
 * @returns {Promise<Object>} Update result
 */
export const removeParticipantFromThread = async (threadId, participantId) => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads/${threadId}/participants/${participantId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error removing participant from thread:', error);
    throw error;
  }
};

/**
 * Subscribe to thread updates
 * @param {string} threadId - The thread ID
 * @param {Function} callback - Callback function for updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToThreadUpdates = async (threadId, callback) => {
  try {
    // For now, return a simple unsubscribe function
    // In a real implementation, this would set up Firestore listeners
    console.log(`Subscribing to thread updates for thread: ${threadId}`);
    
    // Return unsubscribe function
    return () => {
      console.log(`Unsubscribing from thread updates for thread: ${threadId}`);
    };
  } catch (error) {
    console.error('Error subscribing to thread updates:', error);
    throw error;
  }
};

/**
 * Get all threads for a user
 * @param {Object} options - Query options
 * @param {string} options.userId - The user ID
 * @param {string} options.status - Filter by thread status
 * @param {number} options.limit - Limit number of results
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise<Array>} Array of threads
 */
export const getThreads = async (options = {}) => {
  try {
    const token = await getAuthToken();
    const queryParams = new URLSearchParams();
    
    if (options.userId) {
      queryParams.append('userId', options.userId);
    }
    if (options.status) {
      queryParams.append('status', options.status);
    }
    if (options.limit) {
      queryParams.append('limit', options.limit.toString());
    }
    if (options.offset) {
      queryParams.append('offset', options.offset.toString());
    }

    const response = await fetch(`${API_BASE_URL}/internal/messaging/threads?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching threads:', error);
    throw error;
  }
};